import { useQuery } from '@tanstack/react-query'
import { config, hasR2 } from '@/config'
import type { ThreatLevel } from '@/lib/criteria/levels'

// ── Tropical systems (global feed) ───────────────────────────────────────────
export interface TropicalSystem {
  id: string
  name: string
  basin?: string
  classification?: string // "Hurricane" | "Tropical Storm" | "Invest" | ...
  category?: number // Saffir-Simpson, if a hurricane
  windMph?: number
  movement?: string
  /** Center position, if the feed carries it (flat lat/lon or GeoJSON geometry). */
  lat?: number
  lon?: number
}

// ── SPC convective outlook ───────────────────────────────────────────────────
export type SpcCategory = 'NONE' | 'TSTM' | 'MRGL' | 'SLGT' | 'ENH' | 'MDT' | 'HIGH'
export interface SpcOutlook {
  category: SpcCategory
  label: string
  summary?: string
  imageUrl?: string
  validDay?: number
}

const SPC_LABEL: Record<SpcCategory, string> = {
  NONE: 'No severe risk',
  TSTM: 'General thunderstorms',
  MRGL: 'Marginal risk',
  SLGT: 'Slight risk',
  ENH: 'Enhanced risk',
  MDT: 'Moderate risk',
  HIGH: 'High risk',
}

export function spcLevel(cat: SpcCategory): ThreatLevel {
  switch (cat) {
    case 'HIGH':
      return 'extreme'
    case 'MDT':
      return 'severe'
    case 'ENH':
      return 'warning'
    case 'SLGT':
    case 'MRGL':
      return 'watch'
    default:
      return 'info'
  }
}

function str(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k]
    if (typeof v === 'string' && v) return v
    if (typeof v === 'number') return String(v)
  }
  return undefined
}
function num(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k]
    if (typeof v === 'number') return v
    if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v)
  }
  return undefined
}

/** Pull a center lat/lon from flat fields or a GeoJSON Point geometry (tolerant). */
function centerOf(
  rec: Record<string, unknown>,
  o: Record<string, unknown>,
): { lat?: number; lon?: number } {
  const lat = num(o, ['lat', 'latitude', 'centerLat', 'clat'])
  const lon = num(o, ['lon', 'lng', 'long', 'longitude', 'centerLon', 'clon'])
  if (typeof lat === 'number' && typeof lon === 'number') return { lat, lon }
  // GeoJSON: geometry.coordinates is [lon, lat] for a Point.
  const geom = rec.geometry
  if (geom && typeof geom === 'object') {
    const coords = (geom as Record<string, unknown>).coordinates
    if (Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return { lon: coords[0], lat: coords[1] }
    }
  }
  return {}
}

function normalizeTropics(json: unknown): TropicalSystem[] {
  const list: unknown[] = Array.isArray(json)
    ? json
    : ((json as Record<string, unknown>)?.systems as unknown[]) ??
      ((json as Record<string, unknown>)?.storms as unknown[]) ??
      ((json as Record<string, unknown>)?.features as unknown[]) ??
      []
  return list
    .map((item, i): TropicalSystem | null => {
      // Tolerant: feed items may be primitives/null (unknown external shape) — skip those
      // rather than letting `in` throw on a non-object and abort the whole feed.
      const rec = item && typeof item === 'object' ? (item as Record<string, unknown>) : null
      if (!rec) return null
      const o = ('properties' in rec && rec.properties && typeof rec.properties === 'object'
        ? rec.properties
        : rec) as Record<string, unknown>
      const name = str(o, ['name', 'stormName', 'tcName', 'id'])
      if (!name) return null
      const { lat, lon } = centerOf(rec, o)
      return {
        id: str(o, ['id', 'atcfId', 'stormId']) ?? `${name}-${i}`,
        name,
        basin: str(o, ['basin', 'region']),
        classification: str(o, ['classification', 'type', 'status']),
        category: num(o, ['category', 'cat', 'saffirSimpson']),
        windMph: num(o, ['windMph', 'maxWindMph', 'intensity', 'maxWind']),
        movement: str(o, ['movement', 'motion']),
        lat,
        lon,
      }
    })
    .filter((s): s is TropicalSystem => s !== null)
}

function normalizeSpc(json: unknown): SpcOutlook {
  const o = (Array.isArray(json) ? json[0] : json) as Record<string, unknown>
  const raw = (str(o ?? {}, ['category', 'categorical', 'risk', 'maxRisk', 'level']) ?? 'NONE')
    .toUpperCase()
    .replace(/\s+/g, '')
  const category = (['NONE', 'TSTM', 'MRGL', 'SLGT', 'ENH', 'MDT', 'HIGH'] as SpcCategory[]).includes(
    raw as SpcCategory,
  )
    ? (raw as SpcCategory)
    : 'NONE'
  return {
    category,
    label: SPC_LABEL[category],
    summary: str(o ?? {}, ['summary', 'discussion', 'text', 'headline']),
    imageUrl: str(o ?? {}, ['image', 'url', 'img', 'graphic']),
    validDay: num(o ?? {}, ['day', 'validDay']),
  }
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`Feed ${res.status}`)
  return res.json()
}

export function useTropicsQuery() {
  return useQuery({
    queryKey: ['pulse', 'tropics'],
    queryFn: async () => normalizeTropics(await fetchJson(config.r2.feeds.tropics)),
    enabled: hasR2,
    staleTime: 15 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
  })
}

export function useSpcQuery() {
  return useQuery({
    queryKey: ['pulse', 'spc'],
    queryFn: async () => normalizeSpc(await fetchJson(config.r2.feeds.spc)),
    enabled: hasR2,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
  })
}
